import React from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Stack, Typography } from '@mui/material';

import ChannelMarkupActiveMarkupsAccordion from './ChannelMarkupActiveMarkupsAccordion';
import ChannelMarkupClientInitSection from './ChannelMarkupClientInitSection';
import ChannelMarkupEvaluationContextSection from './ChannelMarkupEvaluationContextSection';
import type { ChannelMarkupTesterPanelProps } from './ChannelMarkupTesterPanel.types';
import ChannelMarkupTesterResultCard from './ChannelMarkupTesterResultCard';
import ChannelMarkupTesterRunSection from './ChannelMarkupTesterRunSection';

export default function ChannelMarkupTesterPanel({
  title,
  description,
  summaryId,
  contentId,
  showActiveMarkups,
  activeChannelMarkups,
  isActiveChannelMarkupsLoading,
  testerMarkupMode,
  setTesterMarkupMode,
  onRunPreview,
  selectedCurrencies,
  setSelectedCurrencies,
  isClientInitReady,
  clientInitUpdatedAt,
  clientInitRefreshMessage,
  clientInitRefreshError,
  clientInitConfig,
  isClientInitFetching,
  clientInitMode,
  setClientInitMode,
  tenantBrand,
  handleRefreshClientInit,
  testerResults,
  twoShotTesterResults,
  onRunTwoShotPreview,
  testerResult,
  testerValues,
  effectiveTesterContext,
  registerTester,
  setTesterValue,
  subscriberTierOptions,
  userRoleOptions,
  currencyOptions,
  audienceSearchInput,
  setAudienceSearchInput,
  audienceSearchEnabled,
  audienceOptions,
  isAudienceSearchLoading,
  noAudienceResults,
}: ChannelMarkupTesterPanelProps) {
  const isGeneralModeUnavailable = testerMarkupMode === 'general' && !isClientInitReady;

  return (
    <Accordion>
      <AccordionSummary aria-controls={contentId} id={summaryId}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            Run this in order: load client init, confirm the evaluation context defaults, then inspect each channel
            markup result in the selected preview region.
          </Alert>
          {showActiveMarkups && testerMarkupMode === 'general' && (
            <ChannelMarkupActiveMarkupsAccordion
              contentId={contentId}
              summaryId={summaryId}
              activeChannelMarkups={activeChannelMarkups}
              isActiveChannelMarkupsLoading={isActiveChannelMarkupsLoading}
            />
          )}
          <ChannelMarkupClientInitSection
            clientInitUpdatedAt={clientInitUpdatedAt}
            clientInitRefreshMessage={clientInitRefreshMessage}
            clientInitRefreshError={clientInitRefreshError}
            clientInitConfig={clientInitConfig}
            isClientInitFetching={isClientInitFetching}
            clientInitMode={clientInitMode}
            setClientInitMode={setClientInitMode}
            tenantBrand={tenantBrand}
            handleRefreshClientInit={handleRefreshClientInit}
          />
          <ChannelMarkupEvaluationContextSection
            testerValues={testerValues}
            registerTester={registerTester}
            setTesterValue={setTesterValue}
            subscriberTierOptions={subscriberTierOptions}
            userRoleOptions={userRoleOptions}
            audienceSearchInput={audienceSearchInput}
            setAudienceSearchInput={setAudienceSearchInput}
            audienceSearchEnabled={audienceSearchEnabled}
            audienceOptions={audienceOptions}
            isAudienceSearchLoading={isAudienceSearchLoading}
            noAudienceResults={noAudienceResults}
          />
          <ChannelMarkupTesterRunSection
            testerMarkupMode={testerMarkupMode}
            setTesterMarkupMode={setTesterMarkupMode}
            onRunPreview={onRunPreview}
            currencyOptions={currencyOptions}
            selectedCurrencies={selectedCurrencies}
            setSelectedCurrencies={setSelectedCurrencies}
            isClientInitReady={isClientInitReady}
            isClientInitFetching={isClientInitFetching}
            handleRefreshClientInit={handleRefreshClientInit}
            clientInitConfig={clientInitConfig}
            testerResults={testerResults}
            testerResult={testerResult}
            testerValues={testerValues}
            effectiveTesterContext={effectiveTesterContext}
            isGeneralModeUnavailable={isGeneralModeUnavailable}
          />
          <Accordion disableGutters>
            <AccordionSummary aria-controls="channel-markup-two-shot-content" id="channel-markup-two-shot-header">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Legacy two-shot evaluator
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare the retired stateful round evaluator with the current customer portal result.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails id="channel-markup-two-shot-content">
              <Stack spacing={2}>
                <Alert severity="info" variant="outlined">
                  This uses the deprecated `evaluateChannelMarkupRound` session and offer passes. The main evaluator
                  above is the current customer portal select-then-evaluate flow.
                </Alert>
                <Button variant="contained" onClick={onRunTwoShotPreview}>
                  Check two-shot
                </Button>
                <Stack spacing={2}>
                  {twoShotTesterResults.map((result) => (
                    <ChannelMarkupTesterResultCard
                      key={result.currency}
                      result={result}
                      testerValues={testerValues}
                      effectiveTesterContext={effectiveTesterContext}
                    />
                  ))}
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
